import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check } from 'lucide-react';

const tiers = [
  {
    name: 'GEO Starter',
    price: 'from $800',
    period: 'month / client',
    description: 'Get your client found and cited in AI recommendations.',
    features: [
      'AI visibility audit',
      'Schema markup & structured data implementation',
      'Google Business Profile optimisation',
      'NAP & citation consistency',
      'Monthly AI Share of Voice report',
      'White-label delivery'
    ],
    popular: false
  },
  {
    name: 'GEO + AEO Growth',
    price: 'from $1,500',
    period: 'month / client',
    description: 'Become the authoritative source AI engines quote.',
    features: [
      'Everything in GEO Starter',
      'FAQ & Q&A content strategy',
      'Authoritative content creation (4 pieces/month)',
      'Entity authority building',
      'AI citation source building',
      'Competitor citation analysis'
    ],
    popular: true
  },
  {
    name: 'ChatGPT Ads (Layer 3)',
    price: 'On request',
    period: 'managed service',
    description: 'Paid placements inside ChatGPT responses, managed end-to-end.',
    features: [
      'Campaign setup & management',
      'Contextual & geo targeting',
      'Creative & copy optimisation',
      'Conversion pixel & performance tracking',
      'Available to most US business categories',
      'Some regulated sectors restricted by OpenAI — ask us'
    ],
    popular: false
  }
];

const RankPricingPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="rank-pricing-page">
      <Helmet><title>MarqiRank Pricing — Transparent AI Search Visibility</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-28 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="rank-pricing-hero-title">
            MarqiRank Pricing — <span className="text-[var(--rank-accent)]">Transparent from the Start.</span>
          </h1>
          <p className="body-lg max-w-3xl mx-auto">
            No "request a quote" friction. Per-client pricing you can benchmark against traditional SEO
            today. All prices are per client, per month, delivered fully white-label under your brand.
          </p>
        </div>
      </section>

      {/* Tiers */}
      <section className="pb-24">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className="feature-card relative"
                style={tier.popular ? { border: '1px solid var(--rank-accent)' } : {}}
                data-testid={`rank-pricing-tier-${tier.name.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="px-4 py-1 rounded-full text-sm font-semibold" style={{ background: 'var(--rank-accent)', color: 'var(--rank-bg)' }}>
                      Most Popular
                    </div>
                  </div>
                )}
                <h3 className="h2 text-[var(--text-primary)] mb-3">{tier.name}</h3>
                <div className="mb-4">
                  <span className="h1 text-[var(--rank-accent)]">{tier.price}</span>
                  <span className="text-[var(--text-muted)] block text-sm mt-1">{tier.period}</span>
                </div>
                <p className="body-md mb-6">{tier.description}</p>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className={`w-full ${tier.popular ? 'btn-primary' : 'btn-secondary'}`} data-testid={`rank-pricing-cta-${tier.name.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}>
                  {tier.price === 'On request' ? 'Check Eligibility' : 'Get Started'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center body-md max-w-3xl mx-auto mt-12 text-[var(--text-secondary)] pt-8 border-t border-[var(--border-subtle)]">
            All plans: no media spend required for Layers 1 and 2 · white-label delivery · monthly rolling
            contracts · cancel anytime.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ background: 'rgba(74, 53, 128, 0.07)' }}>
        <div className="container text-center max-w-3xl mx-auto">
          <h2 className="h1 text-[var(--text-primary)] mb-6">See It on One of Your Own Clients — Free.</h2>
          <p className="body-lg mb-10">
            Book a session and we'll run a live AI visibility audit for one of your existing clients — no
            commitment, no charge.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/audit" className="btn-primary glow-effect" data-testid="rank-pricing-final-cta">
              Get a Free AI Visibility Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/rank" className="btn-secondary">Back to MarqiRank</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RankPricingPage;
